import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import authService from '../services/authService';
import { clearAllCached } from '../services/cacheService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);

  const sessionEpochRef = useRef(0);
  const bumpEpoch = () => { sessionEpochRef.current += 1; return sessionEpochRef.current; };

  // 🆕 NUEVO: Estado para manejo de roles
  const [currentRole, setCurrentRole] = useState(null);

  // Referencias para manejar timeouts
  const warningTimeoutRef = useRef(null);
  const expirationTimeoutRef = useRef(null);
  const isRenewingRef = useRef(false);

  // Obtener duración del token desde localStorage o usar default
  const getStoredTokenDuration = () => {
    const stored = localStorage.getItem('tokenDuration');
    return stored ? parseInt(stored, 10) : 15 * 1000;
  };

  const [tokenDuration, setTokenDuration] = useState(getStoredTokenDuration());

  const logout = useCallback(() => {
    bumpEpoch();
    clearTokenTimers();
    clearStoredTokenData();
    clearAllCached();
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setShowExpiryWarning(false);
    // 🆕 NUEVO: Limpiar rol actual al hacer logout
    setCurrentRole(null);
    localStorage.removeItem('currentRole');
    localStorage.removeItem('activeRole');
    isRenewingRef.current = false;
  }, []);

  // 🆕 NUEVO: Helper para formatear datos adicionales que puedan venir de la API externa (UTM)
  const formatUserData = useCallback((u) => {
    if (!u) return u;
    const formatted = { ...u };

    // Si el backend envía la respuesta de la UTM (value) anidada
    const sourceData = formatted.value || formatted;

    // Transferir datos del origen si existen
    if (sourceData.cedula) formatted.cedula = sourceData.cedula;
    if (sourceData.nombres) formatted.nombres = sourceData.nombres;
    if (sourceData.mail_alternativo) formatted.mail_alternativo = sourceData.mail_alternativo;

    // Parsear JSONs que vienen como strings desde la API de la UTM
    if (typeof sourceData.datos_estudio === 'string') {
      try {
        formatted.datos_estudio = JSON.parse(sourceData.datos_estudio);
      } catch (e) {
        console.error("Error parseando datos_estudio", e);
      }
    } else if (sourceData.datos_estudio) {
      formatted.datos_estudio = sourceData.datos_estudio;
    }

    if (typeof sourceData.ingles === 'string') {
      try {
        formatted.ingles = JSON.parse(sourceData.ingles);
      } catch (e) {
        console.error("Error parseando ingles", e);
      }
    } else if (sourceData.ingles) {
      formatted.ingles = sourceData.ingles;
    }

    return formatted;
  }, []);


  function computeEffectiveRole(user) {
    const rolesGlobal = Array.isArray(user?.roles)
      ? user.roles.map(r => String(r).toLowerCase())
      : [];

    const isAdminGlobal =
      rolesGlobal.includes("administrador") ||
      rolesGlobal.includes("admin") ||
      rolesGlobal.includes("superadmin");

    const isAdminFacultad =
      rolesGlobal.includes("admin_facultad") ||
      (Array.isArray(user?.admin_facultades) &&
        user.admin_facultades.some(f =>
          Array.isArray(f?.roles) && f.roles.some(r => String(r).toLowerCase() === "admin")
        ));

    const isCoordFacultad =
      Array.isArray(user?.admin_facultades) &&
      user.admin_facultades.some(f => Array.isArray(f?.roles) && f.roles.some(r => String(r).toLowerCase() === "coordinador"));

    const cr = user?.course_roles || {};
    const hasTeacherFlag = !!cr?.docente || !!cr?.asistente;

    const teacherRoles = Array.isArray(user?.teacher_roles)
      ? user.teacher_roles.map(r => String(r).toLowerCase())
      : [];

    const isTeacher =
      hasTeacherFlag ||
      teacherRoles.includes("docente") ||
      teacherRoles.includes("asistente");

    // ✅ Prioridades
    if (isAdminGlobal) return "administrador";
    if (isAdminFacultad) return "admin_facultad";
    if (isTeacher) return (cr?.asistente ? "asistente" : "docente");
    return "estudiante";
  }


  const handleSessionExpiry = useCallback(() => {
    localStorage.setItem("auth:warning", "0");
    bcRef.current?.postMessage?.({ type: "LOGOUT" });

    setShowExpiryWarning(false);
    logout();
  }, [logout]);


  // Warning time dinámico - configurable
  const getWarningTime = useCallback(() => {
    return Math.min(tokenDuration * 0.4, 60 * 1000);
  }, [tokenDuration]);

  // Función para calcular tiempo restante del token
  const getTokenRemainingTime = () => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return 0;

    const now = Date.now();
    const expiryTime = parseInt(tokenExpiry, 10);
    return Math.max(0, expiryTime - now);
  };

  // Iniciar timer del token con duración específica
  const startTokenTimerWithDuration = useCallback((duration) => {
    // ✅ invalida timers anteriores (epoch nuevo)
    const myEpoch = bumpEpoch();

    clearTokenTimers();

    const warningTime = getWarningTime();

    const safeShowWarning = () => {
      // ✅ si ya hubo refresh/login y cambió epoch, NO mostrar warning viejo
      if (sessionEpochRef.current !== myEpoch) return;
      setShowExpiryWarning(true);
      localStorage.setItem("auth:warning", "1");
      bcRef.current?.postMessage?.({ type: "SHOW_WARNING" });
    };

    const safeExpire = () => {
      // ✅ si ya hubo refresh/login y cambió epoch, NO expirar
      if (sessionEpochRef.current !== myEpoch) return;
      handleSessionExpiry();
    };

    if (duration > warningTime) {
      warningTimeoutRef.current = setTimeout(safeShowWarning, duration - warningTime);
    } else {
      safeShowWarning();
    }

    expirationTimeoutRef.current = setTimeout(safeExpire, duration);
  }, [getWarningTime, handleSessionExpiry]);


  // Iniciar timer del token (usa duración del estado)
  const startTokenTimer = useCallback(() => {
    startTokenTimerWithDuration(tokenDuration);
  }, [startTokenTimerWithDuration, tokenDuration]);

  const hideWarningEverywhere = () => {
    setShowExpiryWarning(false);
    localStorage.setItem("auth:warning", "0");
    bcRef.current?.postMessage?.({ type: "HIDE_WARNING" });
  };


  // 🆕 NUEVO: Funciones para manejo de roles

  // 🔎 Construir lista unificada de roles (globales + por curso)
  const buildAllRoles = useCallback((u) => {
    if (!u) return [];

    // 1) Roles globales de la tabla usuario_roles
    const baseRoles = Array.isArray(u.roles) ? u.roles : [];
    const globalRoles = baseRoles
      .map((r) => {
        if (typeof r === 'string') return r;
        // soportar { nombre: 'administrador' } / { rol: 'estudiante' } etc.
        return r.nombre || r.rol || r.role || '';
      })
      .filter(Boolean)
      .map((r) => r.toLowerCase());

    const rolesSet = new Set(globalRoles);

    // 2) Roles por curso tipo objeto: { docente: true, asistente: false }
    const courseRolesObj = u.course_roles || u.curso_roles;
    if (courseRolesObj && typeof courseRolesObj === 'object' && !Array.isArray(courseRolesObj)) {
      if (courseRolesObj.docente) rolesSet.add('docente');
      if (courseRolesObj.asistente) rolesSet.add('asistente');
    }

    // 3) Roles por curso tipo array: ['docente', 'asistente', ...]
    const teacherRolesArr = u.teacher_roles || u.roles_docente;
    if (Array.isArray(teacherRolesArr)) {
      teacherRolesArr.forEach((r) => {
        const low = String(r).toLowerCase();
        if (low.includes('docente')) rolesSet.add('docente');
        if (low.includes('asistente')) rolesSet.add('asistente');
      });
    }

    // 4) Admin por facultad (si trae admin_facultades)
    const facs = u.admin_facultades;
    if (Array.isArray(facs) && facs.length > 0) {
      rolesSet.add("admin_facultad");
    }

    return Array.from(rolesSet); // siempre en minúsculas
  }, []);


  // dentro de AuthProvider
  const bcRef = useRef(null);

  useEffect(() => {
    // BroadcastChannel es lo mejor para multi-tab
    if (typeof BroadcastChannel !== "undefined") {
      bcRef.current = new BroadcastChannel("auth:session");
    }

    const onMessage = (ev) => {
      const msg = ev?.data;
      if (!msg || !msg.type) return;

      if (msg.type === "SESSION_RENEWED") {
        // otra pestaña renovó: ocultar modal + rearmar timers con expiry real
        setShowExpiryWarning(false);

        if (msg.token) localStorage.setItem("token", msg.token);
        if (msg.refreshToken) localStorage.setItem("refreshToken", msg.refreshToken);

        if (msg.tokenDurationMs) updateTokenDuration(msg.tokenDurationMs);
        if (msg.tokenExpiry) localStorage.setItem("tokenExpiry", String(msg.tokenExpiry));

        const remaining = Math.max(0, Number(localStorage.getItem("tokenExpiry")) - Date.now());
        if (remaining > 0) startTokenTimerWithDuration(remaining);
        else handleSessionExpiry();
      }

      if (msg.type === "SHOW_WARNING") {
        setShowExpiryWarning(true);
      }

      if (msg.type === "HIDE_WARNING") {
        setShowExpiryWarning(false);
      }

      if (msg.type === "LOGOUT") {
        // otra pestaña cerró sesión
        handleSessionExpiry();
      }
    };

    if (bcRef.current) bcRef.current.addEventListener("message", onMessage);

    // Fallback extra: storage event (también sirve si BC no existe)
    const onStorage = (e) => {
      if (e.key === "tokenExpiry") {
        const expiry = Number(e.newValue || 0);
        const remaining = Math.max(0, expiry - Date.now());
        if (remaining <= 0) handleSessionExpiry();
        else startTokenTimerWithDuration(remaining);
      }

      if (e.key === "token" && !e.newValue) {
        handleSessionExpiry();
      }

      if (e.key === "auth:warning") {
        // "1" o "0"
        setShowExpiryWarning(e.newValue === "1");
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      if (bcRef.current) {
        bcRef.current.removeEventListener("message", onMessage);
        bcRef.current.close();
        bcRef.current = null;
      }
    };
  }, [startTokenTimerWithDuration, handleSessionExpiry]); // ok

  // Función para cambiar de rol
  const switchRole = useCallback((roleId, roleName) => {
    try {
      if (!user) {
        console.error('❌ No hay usuario cargado');
        return false;
      }

      const target = (roleName || '').toLowerCase();
      const allRoles = buildAllRoles(user);
      // ['administrador','estudiante','docente','asistente',...]

      const hasRole = allRoles.includes(target);

      if (!hasRole) {
        console.error('❌ Usuario no tiene acceso a este rol:', roleName);
        return false;
      }

      // ✅ Actualizar estado y storage
      setCurrentRole(target);

      localStorage.setItem('currentRole', target);
      localStorage.setItem('activeRole', target); // 👈 AÑADIDO

      return true;
    } catch (error) {
      console.error('❌ Error al cambiar rol:', error);
      return false;
    }
  }, [user, buildAllRoles]);


  // Función para obtener el rol actual
  const getCurrentRole = useCallback(() => {
    if (!user) return null;

    const allRoles = buildAllRoles(user); // unificados
    if (allRoles.length === 0) return null;

    // 1) Si ya hay currentRole en estado y sigue siendo válido, úsalo
    if (currentRole && allRoles.includes(currentRole.toLowerCase())) {
      return currentRole.toLowerCase();
    }

    // 2) Intentar obtener del localStorage
    const savedRole = localStorage.getItem('currentRole');
    if (savedRole) {
      const lowSaved = savedRole.toLowerCase();
      if (allRoles.includes(lowSaved)) {
        setCurrentRole(lowSaved);
        return lowSaved;
      }
    }

    // 3) Si no hay nada válido, usar el primer rol disponible
    const firstRole = allRoles[0]; // ya está en minúsculas
    setCurrentRole(firstRole);
    localStorage.setItem('currentRole', firstRole);
    return firstRole;
  }, [currentRole, user, buildAllRoles]);


  // Función para verificar si el usuario tiene un rol específico
  const hasRole = useCallback((targetRole) => {
    if (!user) return false;
    const target = (targetRole || '').toLowerCase();
    const allRoles = buildAllRoles(user);
    return allRoles.includes(target);
  }, [user, buildAllRoles]);


  const hasPermission = useCallback((permission) => {
    const role = getCurrentRole();

    if (!role) return false;

    const rolePermissions = {
      'administrador': ['all'],
      'admin_facultad': ['manage_faculty_courses', 'view_students', 'manage_courses'],
      'docente': ['manage_courses', 'view_students', 'grade_assignments', 'create_assignments'],
      'estudiante': ['view_courses', 'submit_assignments', 'view_grades', 'view_progress'],
    };

    const userPermissions = rolePermissions[role.toLowerCase()] || [];
    return userPermissions.includes('all') || userPermissions.includes(permission);
  }, [getCurrentRole]);



  // Dentro de AuthContext.jsx

  useEffect(() => {
    const initializeAuth = async () => {
      const initEpoch = sessionEpochRef.current;
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const remainingTime = getTokenRemainingTime();

        if (remainingTime <= 0) {
          throw new Error('Token expirado');
        }

        let usuario = await authService.verifyToken();
      if (sessionEpochRef.current !== initEpoch) return;
      usuario = formatUserData(usuario);
      authService.setCurrentUser(usuario);
      setUser(usuario);
        setIsAuthenticated(true);
        // ✅ Prioridad: rol guardado por el usuario (si es válido)
        const allRoles = buildAllRoles(usuario); // usa tu helper
        const saved = (localStorage.getItem("currentRole") || "").toLowerCase();
        const nextRole =
          (saved && allRoles.includes(saved)) ? saved : computeEffectiveRole(usuario);

        setCurrentRole(nextRole);
        localStorage.setItem("currentRole", nextRole);
        localStorage.setItem("activeRole", nextRole);

        startTokenTimerWithDuration(remainingTime);
      } catch (error) {
        try {
          const refreshResponse = await authService.refreshToken();
          if (sessionEpochRef.current !== initEpoch) return;

          if (refreshResponse && refreshResponse.tokenDurationMs) {
            updateTokenDuration(refreshResponse.tokenDurationMs);
          }

          let usuario = await authService.verifyToken();
          if (sessionEpochRef.current !== initEpoch) return;
          usuario = formatUserData(usuario);
          authService.setCurrentUser(usuario);
          setUser(usuario);
          setIsAuthenticated(true);

          const remaining = Math.max(0, Number(localStorage.getItem("tokenExpiry")) - Date.now());
          if (remaining > 0) startTokenTimerWithDuration(remaining);
          else startTokenTimerWithDuration(tokenDuration);
        } catch (refreshError) {
          clearStoredTokenData();
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [startTokenTimer, startTokenTimerWithDuration]);



  // 🆕 NUEVO: Inicializar rol cuando el usuario cambia
  useEffect(() => {
    if (!user) return;
    if (currentRole) return;

    const allRoles = buildAllRoles(user);
    const saved = (localStorage.getItem("currentRole") || "").toLowerCase();
    const nextRole =
      (saved && allRoles.includes(saved)) ? saved : computeEffectiveRole(user);

    setCurrentRole(nextRole);
    localStorage.setItem("currentRole", nextRole);
    localStorage.setItem("activeRole", nextRole);
  }, [user, currentRole, buildAllRoles]);

  // Actualizar duración del token y persistirla
  const updateTokenDuration = (newDuration) => {
    setTokenDuration(newDuration);
    localStorage.setItem('tokenDuration', newDuration.toString());

    // Calcular y guardar tiempo de expiración
    const expiryTime = Date.now() + newDuration;
    localStorage.setItem('tokenExpiry', expiryTime.toString());
  };

  // Limpiar datos del token almacenados
  const clearStoredTokenData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenDuration');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('currentRole'); // 🆕 NUEVO: Limpiar rol
    localStorage.removeItem('activeRole');
    localStorage.setItem("auth:warning", "0");
  };

  // Limpiar todos los timers
  const clearTokenTimers = () => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (expirationTimeoutRef.current) {
      clearTimeout(expirationTimeoutRef.current);
      expirationTimeoutRef.current = null;
    }
  };

  // Limpiar timers al desmontar
  useEffect(() => {
    return () => {
      clearTokenTimers();
    };
  }, []);

  const login = async (email, password) => {
    const loginEpoch = bumpEpoch();
    clearTokenTimers();
    setShowExpiryWarning(false);
    setCurrentRole(null);
    try {
      clearTokenTimers();
      setShowExpiryWarning(false);
      localStorage.setItem("auth:warning", "0");
      bcRef.current?.postMessage?.({ type: "HIDE_WARNING" });
      isRenewingRef.current = false;

      const response = await authService.login(email, password);
      if (sessionEpochRef.current !== loginEpoch) {
        throw new Error("Inicio de sesión cancelado");
      }
      const { token, refreshToken, tokenDurationMs } = response;

      // 👇 OJO: algunos backends devuelven {usuario} y otros usuario plano
      const rawUser = response?.usuario ? response.usuario : response;
      let usuario = rawUser?.usuario ? rawUser.usuario : rawUser;

      // ✅ Formatear datos que puedan venir de la API UTM
      usuario = formatUserData(usuario);
      if (response?.password_must_change != null && usuario) {
        usuario.password_must_change = Boolean(response.password_must_change);
      }
      if (response?.must_change_password != null && usuario) {
        usuario.must_change_password = Boolean(response.must_change_password);
      }
      clearAllCached();
      authService.setCurrentUser(usuario);

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      setUser(usuario);
      setIsAuthenticated(true);
      setShowExpiryWarning(false);

      // ✅ setear rol en login también
      const allRoles = buildAllRoles(usuario);
      const saved = (localStorage.getItem("currentRole") || "").toLowerCase();
      const nextRole =
        (saved && allRoles.includes(saved)) ? saved : computeEffectiveRole(usuario);

      setCurrentRole(nextRole);
      localStorage.setItem("currentRole", nextRole);
      localStorage.setItem("activeRole", nextRole);

      // timers ✅ FIX: usar siempre startTokenTimerWithDuration con la duración real
      if (tokenDurationMs) {
        updateTokenDuration(tokenDurationMs);
        startTokenTimerWithDuration(tokenDurationMs);
      } else {
        const defaultDuration = 15 * 60 * 1000; // 🔥 recomendado 15 minutos (no 15 segundos)
        updateTokenDuration(defaultDuration);
        startTokenTimerWithDuration(defaultDuration);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  // 🆕 NUEVO: refrescar usuario desde /api/auth/verify
  const refreshUser = useCallback(async () => {
    try {
      let usuario = await authService.verifyToken();
      usuario = formatUserData(usuario);
      authService.setCurrentUser(usuario);

      setUser(usuario);

      const allRoles = buildAllRoles(usuario);
      const saved = (localStorage.getItem("currentRole") || "").toLowerCase();
      const current = (currentRole || "").toLowerCase();

      const nextRole =
        (current && allRoles.includes(current)) ? current :
          (saved && allRoles.includes(saved)) ? saved :
            computeEffectiveRole(usuario);

      setCurrentRole(nextRole);
      localStorage.setItem("currentRole", nextRole);
      localStorage.setItem("activeRole", nextRole);

      return usuario;
    } catch (e) {
      console.error("❌ Error refrescando usuario:", e);
      throw e;
    }
  }, [buildAllRoles, currentRole]);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const result = await authService.changePassword({ currentPassword, newPassword });

    setUser((prev) => prev ? {
      ...prev,
      password_must_change: false,
      requiere_cambio_password: false,
      must_change_password: false,
    } : prev);

    return result;
  }, []);


  // Manejar cuando el usuario elige continuar
  const handleContinueSession = async () => {
    if (isRenewingRef.current) return;

    // ✅ invalida timeouts actuales inmediatamente
    const renewEpoch = bumpEpoch();
    clearTokenTimers();

    try {
      isRenewingRef.current = true;

      setShowExpiryWarning(false);
      localStorage.setItem("auth:warning", "0");
      bcRef.current?.postMessage?.({ type: "HIDE_WARNING" });

      const refreshResponse = await authService.refreshToken();

      // ⚠️ Tu backend /refresh NO manda tokenDurationMs hoy.
      // Mejor usar expiry real: si no llega, usamos el tokenDuration guardado.
      const newDuration = refreshResponse?.tokenDurationMs ?? tokenDuration;

      updateTokenDuration(newDuration);

      const expiry = Number(localStorage.getItem("tokenExpiry")) || (Date.now() + newDuration);
      const remaining = Math.max(0, expiry - Date.now());
      if (remaining <= 0) {
        handleSessionExpiry();
        return;
      }

      startTokenTimerWithDuration(remaining);

      bcRef.current?.postMessage?.({
        type: "SESSION_RENEWED",
        token: refreshResponse?.token,
        refreshToken: refreshResponse?.refreshToken,
        tokenDurationMs: newDuration,
        tokenExpiry: expiry,
      });
    } catch (error) {
      if (sessionEpochRef.current === renewEpoch) {
        handleSessionExpiry();
      }
    } finally {
      isRenewingRef.current = false;
    }
  };



  // 🆕 NUEVO: Verificar si el registro por cámara está activo
  const isCameraRegistrationActive = useCallback(() => {
    return user?.registro_camara_activo !== false;
  }, [user]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refreshUser,
    changePassword,
    showExpiryWarning,
    handleContinueSession,
    handleSessionExpiry,
    getWarningTime,
    tokenDuration,
    getTokenRemainingTime,
    // 🆕 NUEVO: Funciones de roles
    currentRole,
    switchRole,
    hasPermission,
    hasRole,
    getCurrentRole,
    isCameraRegistrationActive,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
